/*
 * an45-pdp-offer.js — the free-gift line INSIDE the product page (2026-08-25).
 *
 * Phase 2 of the ads→PDP funnel. Until now the only free-gift surface on a PDP
 * was the gallery offer card (a static image); the buy box said nothing, so a
 * shopper arriving from a gift-led ad saw no confirmation of the promise next to
 * the price. This renders that confirmation, and it is CART-AWARE.
 *
 * ONE TRUTH: every fact comes from <script id="an45-gift-config"> and the real
 * allocation engine (window.AN45GiftEngine) — the same pair the cart allocates
 * from and the offer ladder displays from. Nothing about an offer is written
 * here, so an offer edit reaches this automatically. Like the ladder, this is
 * READ-ONLY: it never touches cart lines, it only reports what the engine says.
 *
 * Three states per product, decided by asking the engine what changes if this
 * product is added to the CURRENT cart:
 *   earned    — adding it awards gift(s) now                  ("Free X — worth ₹N")
 *   unlocked  — the cart already earns them (shopper is back) ("X — already unlocked")
 *   partial   — this product is part of a combo that needs    ("Add the Conditioner
 *               something else                                  to unlock X")
 * A product in no offer at all (the Hand Wash, the hamper) renders NOTHING —
 * never invent a promise the engine will not honour.
 *
 * Boot order is load-bearing, same trap the ladder hit (WEBSITE.md §10): this
 * script is deferred inside <main>, the config + engine render at the END of
 * <body>, and deferred scripts run in document order — so we wait for
 * DOMContentLoaded (fires only after every deferred script) plus a bounded retry.
 */
(function () {
  'use strict';

  var ROOT_SEL = '[data-an45-pdp-offer]';
  var booted = false;

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  function money(n) {
    // Matches the store's own display for whole rupees (gift worths are integers).
    return '₹' + String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function shortTitle(t) {
    return String(t || '').replace(/^AN45°?\s*/i, '').trim();
  }

  /* Every product handle named anywhere in an offer's `when` tree. Used to decide
   * whether an offer has anything to do with the product being viewed. */
  function handlesIn(cond, out) {
    out = out || [];
    if (!cond || typeof cond !== 'object') return out;
    if (cond.has) out.push(cond.has);
    if (cond.count && cond.count.of) [].push.apply(out, cond.count.of);
    ['all', 'any'].forEach(function (k) {
      if (Array.isArray(cond[k])) cond[k].forEach(function (c) { handlesIn(c, out); });
    });
    return out;
  }

  function giftsWorth(config, handles) {
    return handles.reduce(function (s, h) {
      var g = config.gifts && config.gifts[h];
      return s + (g && g.worth ? Number(g.worth) : 0);
    }, 0);
  }

  function giftNames(config, handles) {
    return handles.map(function (h) {
      var g = config.gifts && config.gifts[h];
      return shortTitle(g ? g.title : h);
    });
  }

  function giftImage(config, handles) {
    for (var i = 0; i < handles.length; i++) {
      var g = config.gifts && config.gifts[handles[i]];
      if (g && g.image) return g.image;
    }
    return '';
  }

  function listSentence(names) {
    if (names.length <= 1) return names[0] || '';
    if (names.length === 2) return names[0] + ' + ' + names[1];
    return names.slice(0, -1).join(', ') + ' + ' + names[names.length - 1];
  }

  /* Read the cart as {handle: qty}. Gift lines are excluded — they are an OUTPUT
   * of the engine, never an input to it. */
  function readCart(cb) {
    fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart.js' : '/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        var qty = {};
        if (cart && Array.isArray(cart.items)) {
          cart.items.forEach(function (it) {
            var h = it.handle;
            if (!h || /^an45-gift-/.test(h)) return;
            qty[h] = (qty[h] || 0) + it.quantity;
          });
        }
        cb(qty);
      })
      .catch(function () { cb({}); });
  }

  function plan(engine, config, qty) {
    try {
      return engine.computeGiftPlan({ qty: qty, choiceKeepId: null }, config).gifts || [];
    } catch (e) {
      return [];
    }
  }

  /* What to say for this product, given the live cart. */
  function decide(engine, config, handle, cartQty) {
    var offers = (config.offers || []).filter(function (o) {
      return handlesIn(o.when).indexOf(handle) !== -1;
    });
    if (!offers.length) return null;           // no offer touches this product

    var withMe = {};
    Object.keys(cartQty).forEach(function (k) { withMe[k] = cartQty[k]; });
    withMe[handle] = (withMe[handle] || 0) + 1;

    var before = plan(engine, config, cartQty);
    var after = plan(engine, config, withMe);
    var gained = after.filter(function (g) { return before.indexOf(g) === -1; });

    if (gained.length) return { state: 'earned', gifts: gained };
    if (after.length && before.length && after.join() === before.join() && cartQty[handle]) {
      return { state: 'unlocked', gifts: after };
    }

    /* Nothing new yet — name the CHEAPEST missing step, i.e. the offer needing the
     * fewest extra products, breaking ties by the richer gift. Sorting by worth
     * alone told a shampoo shopper to add TWO sunscreens (the ₹1,147 tier) when
     * adding one conditioner unlocks ₹799 — a worse ask and a worse message.
     * `unlock` is the offer's own declared trigger order, so we stay inside the
     * config rather than inventing a path. */
    var best = null;
    offers.forEach(function (o) {
      if (!o.label || !Array.isArray(o.unlock)) return;
      var missing = o.unlock.filter(function (h) { return h !== handle && !withMe[h]; });
      if (!missing.length) return;
      var worth = giftsWorth(config, o.gifts || []);
      var better = !best || missing.length < best.missing.length ||
        (missing.length === best.missing.length && worth > best.worth);
      if (better) best = { offer: o, missing: missing, worth: worth };
    });
    if (best) return { state: 'partial', gifts: best.offer.gifts || [], missing: best.missing };
    return null;
  }

  var ICON =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" ' +
    'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<path d="M20 12v9H4v-9"/><path d="M2 7h20v5H2z"/><path d="M12 21V7"/>' +
    '<path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/>' +
    '<path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/></svg>';

  function html(config, d, products) {
    var names = listSentence(giftNames(config, d.gifts));
    var worth = giftsWorth(config, d.gifts);
    var img = giftImage(config, d.gifts);
    var eyebrow, line;

    if (d.state === 'earned') {
      eyebrow = d.gifts.length > 1 ? 'Free gifts with this product' : 'Free gift with this product';
      line = '<strong>' + esc(names) + '</strong>' + (worth ? ' — worth ' + money(worth) : '') +
        '<span class="an45-pdpo__note">Added automatically to your cart — no code needed.</span>';
    } else if (d.state === 'unlocked') {
      eyebrow = 'Free gift unlocked';
      line = '<strong>' + esc(names) + '</strong>' + (worth ? ' — worth ' + money(worth) : '') +
        '<span class="an45-pdpo__note">Already in your cart.</span>';
    } else {
      var miss = listSentence(d.missing.map(function (h) {
        var p = products && products[h];
        return shortTitle(p ? p.title : h);
      }));
      eyebrow = 'Unlock a free gift';
      line = 'Add the <strong>' + esc(miss) + '</strong> to unlock the <strong>' + esc(names) + '</strong>' +
        (worth ? ' — worth ' + money(worth) : '') +
        '<span class="an45-pdpo__note">Added automatically once both are in your cart.</span>';
    }

    return (
      '<div class="an45-pdpo__in">' +
      (img
        ? '<img class="an45-pdpo__img" src="' + esc(img) + '" alt="" width="72" height="72" loading="lazy">'
        : '<span class="an45-pdpo__ic">' + ICON + '</span>') +
      '<div class="an45-pdpo__txt"><span class="an45-pdpo__eyebrow">' + esc(eyebrow) + '</span>' +
      '<p class="an45-pdpo__line">' + line + '</p></div></div>'
    );
  }

  function init() {
    var roots = document.querySelectorAll(ROOT_SEL);
    if (!roots.length) return;

    var cfgEl = document.getElementById('an45-gift-config');
    var engine = window.AN45GiftEngine;
    // Config + engine exist only when settings.cart_type == 'drawer' (theme.liquid).
    // Without them we cannot state a truthful offer, so render nothing at all.
    if (!cfgEl || !engine) return;

    var config;
    try { config = JSON.parse(cfgEl.textContent); } catch (e) {
      console.warn('[an45-pdp-offer] bad config JSON', e);
      return;
    }

    function paint(cartQty) {
      roots.forEach(function (root) {
        var handle = root.getAttribute('data-handle');
        if (!handle) return;
        var d = decide(engine, config, handle, cartQty);
        if (!d) { root.hidden = true; root.innerHTML = ''; return; }
        var next = html(config, d, config.products);
        if (root.getAttribute('data-last') === next) { root.hidden = false; return; }
        root.setAttribute('data-last', next);
        root.innerHTML = next;
        root.hidden = false;
      });
    }

    paint({});                       // paint the standalone promise immediately…
    readCart(paint);                 // …then correct it against the real cart

    // Keep in step with the cart the same way every other GWP surface does.
    document.addEventListener('cart:refresh', function () { readCart(paint); });
    if (window.PUB_SUB_EVENTS && typeof window.subscribe === 'function') {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, function () { readCart(paint); });
    }
  }

  function tryBoot() {
    if (booted) return true;
    if (!document.getElementById('an45-gift-config') || !window.AN45GiftEngine) return false;
    booted = true;
    init();
    return true;
  }

  function bootWithRetry(n) {
    if (tryBoot() || n <= 0) return;
    setTimeout(function () { bootWithRetry(n - 1); }, 100);
  }

  function ready(fn) {
    if (document.readyState === 'complete') { fn(); return; }
    document.addEventListener('DOMContentLoaded', fn, { once: true });
    window.addEventListener('load', fn, { once: true }); // tryBoot is idempotent
  }

  ready(function () { bootWithRetry(30); });
})();
